/*
  SherpaHolidays Nepal map (September 2026), for sections/sh-nepal-map.liquid.

  The markup is complete without this file: the relief, every route, the
  peaks and the trip list with real links. This adds, all in answer to a
  visitor's pointer, tap or keyboard (nothing follows the scroll):
    - a trip card for the route pointed at, tapped or focused in the list
      (a sheet from the bottom of the screen on phones and small tablets),
    - the climber from the altitude charts walking along that route,
      towards the point pointed at, calm and interruptible like the charts,
    - zoom to a route on click or tap, with sharper relief tiles,
    - filters by trip type and length,
    - a link to one route (?route=everest, with &show=3d to open it in 3D)
      and "Copy link",
    - the "View in 3D" button, shown only where WebGL works; the 3D view
      itself (sh-nepal-map-3d.js) is loaded only when it is tapped or a
      link asks for it.
  With reduced motion the climber stands at once where it is sent and the
  map jumps instead of gliding.
*/
(function () {
  'use strict';
  if (window.__shNepalMap) return;
  window.__shNepalMap = true;

  var VW = 1200;
  var VH = 709;
  var TILE = 300; /* map units per zoom tile (1200 px of the 4800 px relief) */
  var TCOLS = 4;
  var TROWS = 3;
  var DEEP = 1.6; /* zoom from which the sharper tiles show */
  var FLY_MS = 750;

  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var mqWide = window.matchMedia ? window.matchMedia('(min-width: 900px), (min-width: 640px) and (max-height: 500px)') : null;

  function reduced() {
    return !!(mqReduce && mqReduce.matches) || !('requestAnimationFrame' in window);
  }

  function isWide() {
    return !!(mqWide && mqWide.matches);
  }

  /* the kit ease, cubic-bezier(0.22, 0.61, 0.36, 1) */
  function bezier(x1, y1, x2, y2) {
    function bx(t) { return ((1 - 3 * x2 + 3 * x1) * t + (3 * x2 - 6 * x1)) * t * t + 3 * x1 * t; }
    function by(t) { return ((1 - 3 * y2 + 3 * y1) * t + (3 * y2 - 6 * y1)) * t * t + 3 * y1 * t; }
    function dx(t) { return 3 * (1 - 3 * x2 + 3 * x1) * t * t + 2 * (3 * x2 - 6 * x1) * t + 3 * x1; }
    return function (x) {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      var t = x;
      for (var i = 0; i < 8; i++) {
        var e = bx(t) - x;
        if (Math.abs(e) < 1e-5) break;
        var d = dx(t);
        if (Math.abs(d) < 1e-6) break;
        t -= e / d;
      }
      return by(Math.min(1, Math.max(0, t)));
    };
  }
  var ease = bezier(0.22, 0.61, 0.36, 1);

  /* A route's path ("M x y l dx dy ...", several subpaths where a trip
     flies or drives between walks) as points with the distance walked;
     the border's path closes with z. */
  function Route(key, el) {
    this.key = key;
    this.el = el;
    var xs = [];
    var ys = [];
    var ss = [];
    var jump = [];
    var tok = (el.getAttribute('d') || '').match(/[MmLlZz]|-?\d*\.?\d+(?:e[-+]?\d+)?/g) || [];
    var cmd = 'M';
    var x = 0;
    var y = 0;
    var s = 0;
    var first = true;
    var mx = 0;
    var my = 0;
    for (var i = 0; i < tok.length; ) {
      var c = tok[i];
      if (/[Zz]/.test(c)) {
        if (xs.length) {
          s += Math.sqrt((mx - x) * (mx - x) + (my - y) * (my - y));
          jump.push(false);
          xs.push(mx);
          ys.push(my);
          ss.push(s);
          x = mx;
          y = my;
        }
        i++;
        continue;
      }
      if (/[MmLl]/.test(c)) {
        cmd = c;
        first = cmd === 'M' || cmd === 'm';
        i++;
        continue;
      }
      var a = +tok[i];
      var b = +tok[i + 1];
      i += 2;
      if (cmd === 'm' || cmd === 'l') {
        a += x;
        b += y;
      }
      if (xs.length && !first) s += Math.sqrt((a - x) * (a - x) + (b - y) * (b - y));
      jump.push(first && xs.length > 0);
      xs.push(a);
      ys.push(b);
      ss.push(s);
      x = a;
      y = b;
      if (first) {
        first = false;
        mx = a;
        my = b;
        if (cmd === 'M') cmd = 'L';
        else if (cmd === 'm') cmd = 'l';
      }
    }
    this.xs = xs;
    this.ys = ys;
    this.ss = ss;
    this.jump = jump;
    /* the subpath each point is on */
    var subs = [];
    for (var q = 0, sub = 0; q < jump.length; q++) {
      if (jump[q]) sub++;
      subs.push(sub);
    }
    this.subs = subs;
    this.len = s;
    this.rank = parseFloat(el.getAttribute('data-p')) || 0;
    var goal = parseFloat(el.getAttribute('data-goal'));
    this.goal = goal > 0 && goal <= s ? goal : s;
    var x0 = Infinity;
    var y0 = Infinity;
    var x1 = -Infinity;
    var y1 = -Infinity;
    for (var j = 0; j < xs.length; j++) {
      if (xs[j] < x0) x0 = xs[j];
      if (xs[j] > x1) x1 = xs[j];
      if (ys[j] < y0) y0 = ys[j];
      if (ys[j] > y1) y1 = ys[j];
    }
    this.box = [x0, y0, x1, y1];
  }

  /* the point at distance s along the route */
  Route.prototype.at = function (s) {
    var ss = this.ss;
    var n = ss.length;
    if (s <= 0 || n < 2) return { x: this.xs[0], y: this.ys[0] };
    if (s >= this.len) return { x: this.xs[n - 1], y: this.ys[n - 1] };
    var lo = 0;
    var hi = n - 1;
    while (hi - lo > 1) {
      var mid = (lo + hi) >> 1;
      if (ss[mid] <= s) lo = mid;
      else hi = mid;
    }
    /* skip the zero-length jumps between subpaths */
    while (hi < n - 1 && this.jump[hi]) {
      lo = hi;
      hi++;
    }
    var d = ss[hi] - ss[lo];
    var f = d > 0 ? (s - ss[lo]) / d : 0;
    return { x: this.xs[lo] + (this.xs[hi] - this.xs[lo]) * f, y: this.ys[lo] + (this.ys[hi] - this.ys[lo]) * f };
  };

  /* the subpath the point at distance s is drawn on, found as at() finds
     it: where two subpaths meet, the later one */
  Route.prototype.sub = function (s) {
    var ss = this.ss;
    var n = ss.length;
    if (s <= 0 || n < 2) return 0;
    if (s >= this.len) return this.subs[n - 1];
    var lo = 0;
    var hi = n - 1;
    while (hi - lo > 1) {
      var mid = (lo + hi) >> 1;
      if (ss[mid] <= s) lo = mid;
      else hi = mid;
    }
    while (hi < n - 1 && this.jump[hi]) hi++;
    return this.subs[hi];
  };

  /* the nearest point of the route to (px, py): squared distance and s */
  Route.prototype.near = function (px, py) {
    var xs = this.xs;
    var ys = this.ys;
    var best = Infinity;
    var bs = 0;
    for (var i = 1; i < xs.length; i++) {
      if (this.jump[i]) continue;
      var ax = xs[i - 1];
      var ay = ys[i - 1];
      var dx = xs[i] - ax;
      var dy = ys[i] - ay;
      var l2 = dx * dx + dy * dy;
      var t = l2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
      var qx = ax + dx * t - px;
      var qy = ay + dy * t - py;
      var d2 = qx * qx + qy * qy;
      if (d2 < best) {
        best = d2;
        bs = this.ss[i - 1] + (this.ss[i] - this.ss[i - 1]) * t;
      }
    }
    return { d2: best, s: bs };
  };

  /* ---------- the readout beside the climber ----------
     The itinerary stop the climber is at: the days, the place, its height
     and the change from the stop before, as the trip page's altitude
     chart has them (the list's data-stops, days|name|height|about|
     positions per stop in itinerary order, built by the section from the
     chart data and checked against the itinerary the same way). Between
     stops it names the nearest one on the same stretch of path, so it
     always names a real stop. Trips without a chart name their places,
     with a height only where the itinerary states it. The same readout
     and the same rules serve the 3D view. */
  function num(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function readStops(raw) {
    if (!raw) return null;
    var list = [];
    var marks = [];
    var prev = null;
    var es = raw.split(';');
    for (var i = 0; i < es.length; i++) {
      var f = es[i].split('|');
      if (f.length < 5 || !f[1]) continue;
      var days = f[0].replace(/^\s+|\s+$/g, '');
      var m = parseInt(f[2].replace(/,/g, ''), 10) || 0;
      var about = f[3].indexOf('a') >= 0;
      var st = { days: days, name: f[1] + (f[3].indexOf('o') >= 0 ? ' (optional)' : ''), height: '', change: '', say: '', on: false };
      var said = [days.replace('-', ' to '), st.name];
      if (m > 0) {
        var ab = about ? 'about ' : '';
        st.height = ab + num(m) + ' m (' + num(Math.round(m * 3.28084)) + ' ft)';
        said.push(ab + num(m) + ' m (' + num(Math.round(m * 3.28084)) + ' ft)');
        /* the change only between stops of the chart, which has days */
        if (days && prev) {
          var dm = m - prev.m;
          var cab = about || prev.about ? 'about ' : '';
          if (!dm) {
            st.change = 'same height as ' + prev.name;
            said.push(st.change);
          } else {
            st.change = cab + (dm > 0 ? '+' : '−') + num(Math.abs(dm)) + ' m from ' + prev.name;
            said.push(cab + num(Math.abs(dm)) + ' m ' + (dm > 0 ? 'up' : 'down') + ' from ' + prev.name);
          }
        }
        prev = { m: m, about: about, name: f[1] };
      }
      st.say = said.filter(Boolean).join(', ');
      var ps = f[4] ? f[4].split(',') : [];
      for (var j = 0; j < ps.length; j++) {
        var p = ps[j].split('/');
        var s = parseFloat(p[0]);
        if (!(s >= 0)) continue;
        marks.push({ s: s, k: parseInt(p[1], 10) || 0, i: list.length });
        st.on = true;
      }
      list.push(st);
    }
    if (!marks.length) return null;
    marks.sort(function (a, b) {
      return a.s - b.s || a.k - b.k;
    });
    /* one step per stop where the path passes it twice close together */
    var steps = [];
    for (var q = 0; q < marks.length; q++) {
      var last = steps[steps.length - 1];
      if (!last || last.i !== marks[q].i || marks[q].s - last.s >= 0.5) steps.push(marks[q]);
    }
    return { list: list, marks: marks, steps: steps };
  }

  /* the stop nearest distance s on the same stretch of path, or -1 */
  function stopAt(rt, ro, s) {
    if (!rt || !ro) return -1;
    var k = rt.sub(s);
    var best = -1;
    var bd = Infinity;
    for (var i = 0; i < ro.marks.length; i++) {
      var m = ro.marks[i];
      if (m.k !== k) continue;
      var d = Math.abs(m.s - s);
      if (d < bd) {
        bd = d;
        best = m.i;
      }
    }
    return best;
  }

  /* the next stop along the route from distance cur (dir 1), or the one
     before (dir -1), for the keyboard */
  function nextStop(ro, cur, dir) {
    if (!ro) return null;
    var st = ro.steps;
    var i;
    if (dir > 0) {
      for (i = 0; i < st.length; i++) if (st[i].s > cur + 0.02) return st[i];
    } else {
      for (i = st.length - 1; i >= 0; i--) if (st[i].s < cur - 0.02) return st[i];
    }
    return null;
  }

  /* the readout: every stop of the route in one grid cell, only the
     current one visible, so it keeps one size along the whole route and
     nothing shifts as it changes */
  function Readout(parent) {
    var el = document.createElement('div');
    el.className = 'shm__ro';
    el.setAttribute('aria-hidden', 'true');
    parent.appendChild(el);
    this.el = el;
    this.ro = null;
    this.items = [];
    this.cur = -1;
    this.w = 0;
    this.h = 0;
    this.on = false;
    this.side = '';
    this.o = 0;
    this.at = '';
    this.inkOf = '';
    this.inkBox = null;
  }

  Readout.prototype.set = function (ro) {
    if (ro === this.ro) return;
    this.ro = ro;
    this.cur = -1;
    this.w = 0;
    this.inkOf = '';
    this.side = '';
    this.o = 0;
    this.items = [];
    this.el.textContent = '';
    this.show(false);
    if (!ro) return;
    var lines = [['days', 'd'], ['name', 'n'], ['height', 'h'], ['change', 'c']].filter(function (l) {
      return ro.list.some(function (st) {
        return st.on && st[l[0]];
      });
    });
    for (var i = 0; i < ro.list.length; i++) {
      var st = ro.list[i];
      var p = document.createElement('p');
      p.className = 'shm__ro-i';
      /* a stop the path never passes never shows, and takes no room */
      p.hidden = !st.on;
      for (var j = 0; j < lines.length; j++) {
        var sp = document.createElement('span');
        sp.className = 'shm__ro-' + lines[j][1];
        sp.textContent = st[lines[j][0]] || ' ';
        p.appendChild(sp);
      }
      this.el.appendChild(p);
      this.items.push(p);
    }
  };

  Readout.prototype.pick = function (i) {
    if (i === this.cur) return;
    if (this.cur >= 0) this.items[this.cur].classList.remove('is-cur');
    this.cur = i;
    if (i >= 0) this.items[i].classList.add('is-cur');
  };

  Readout.prototype.show = function (on) {
    if (on === this.on) return;
    this.on = on;
    this.el.classList.toggle('is-on', on);
  };

  Readout.prototype.size = function () {
    if (this.w) return;
    this.w = this.el.offsetWidth;
    this.h = this.el.offsetHeight;
  };

  /* Where the readout goes, for the climber's feet at (fx, fy) and its
     figure in box cb around them: beside it, then above or below it,
     then further out, then each of those slid a little along the figure
     (up or down beside it, sideways above or below it), keeping the spot
     it had while that stays clear. Where none is clear (a phone's small
     map), a corner of the map.
     A spot is clear when the readout is inside the view (w x h), off
     the busy boxes (buttons, the trip card) and the route line does not
     come within 5 px of it (line(box)). Spots clear of the border too
     (soft(box)) come first. Null where no spot is clear: it never covers
     the route. */
  var RO_GAP = 8;
  var RO_SIDES = ['e', 'w', 'ne', 'nw', 'se', 'sw', 'n', 's', 'fe', 'fw', 'fn', 'fs'];
  var RO_SLIDE = [0, 12, -12, 24, -24, 36, -36, 48, -48];
  var RO_CORNERS = ['ctl', 'ctr', 'cbl', 'cbr'];

  function roBox(r, sd, fx, fy, cb) {
    var w = r.w;
    var h = r.h;
    var x0 = fx + cb[0];
    var x1 = fx + cb[2];
    var y0 = fy + cb[1];
    var y1 = fy + cb[3];
    var my = (y0 + y1) / 2 - h / 2;
    var far = sd.charAt(0) === 'f' ? 28 : 0;
    var g = RO_GAP + far;
    var d = far ? sd.charAt(1) : sd;
    var x;
    var y;
    if (d === 'e' || d === 'ne' || d === 'se') x = x1 + g;
    else if (d === 'w' || d === 'nw' || d === 'sw') x = x0 - g - w;
    else x = fx - w / 2;
    if (d === 'e' || d === 'w') y = my;
    else if (d === 'ne' || d === 'nw') y = y0 - h + 4;
    else if (d === 'se' || d === 'sw') y = y1 - 4;
    else if (d === 'n') y = y0 - g - h;
    else y = y1 + g;
    x = Math.round(x);
    y = Math.round(y);
    return [x, y, x + w, y + h];
  }

  function roSpot(r, fx, fy, cb, W, H, busy, line, soft) {
    function spot(sd, o) {
      if (sd.charAt(0) === 'c') {
        var cx = sd.charAt(2) === 'l' ? 8 : W - 8 - r.w;
        var cy = sd.charAt(1) === 't' ? 8 : H - 8 - r.h;
        return [cx, cy, cx + r.w, cy + r.h];
      }
      var b = roBox(r, sd, fx, fy, cb);
      if (!o) return b;
      var d = sd.replace('f', '');
      if (d === 'n' || d === 's') return [b[0] + o, b[1], b[2] + o, b[3]];
      return [b[0], b[1] + o, b[2], b[3] + o];
    }
    function clear(b, strict) {
      if (b[0] < 6 || b[1] < 6 || b[2] > W - 6 || b[3] > H - 6) return false;
      for (var i = 0; i < busy.length; i++) {
        var o = busy[i];
        if (b[0] < o[2] && b[2] > o[0] && b[1] < o[3] && b[3] > o[1]) return false;
      }
      var g = [b[0] - 5, b[1] - 5, b[2] + 5, b[3] + 5];
      return !line(g) && !(strict && soft(g));
    }
    for (var pass = soft ? 0 : 1; pass < 2; pass++) {
      var b;
      if (r.side && r.side.charAt(0) !== 'c') {
        b = spot(r.side, r.o);
        if (clear(b, !pass)) return { side: r.side, o: r.o, b: b };
      }
      for (var j = 0; j < RO_SLIDE.length; j++) {
        for (var i = 0; i < RO_SIDES.length; i++) {
          if (RO_SIDES[i] === r.side && RO_SLIDE[j] === r.o) continue;
          b = spot(RO_SIDES[i], RO_SLIDE[j]);
          if (clear(b, !pass)) return { side: RO_SIDES[i], o: RO_SLIDE[j], b: b };
        }
      }
    }
    for (pass = soft ? 0 : 1; pass < 2; pass++) {
      for (var c = 0; c < RO_CORNERS.length; c++) {
        b = spot(RO_CORNERS[c], 0);
        if (clear(b, !pass)) return { side: RO_CORNERS[c], o: 0, b: b };
      }
    }
    return null;
  }

  /* the part of box b (the readout's spot) the current stop's words
     cover, for fading the map labels under it: the box also keeps room
     for lines only other stops use */
  Readout.prototype.ink = function (b) {
    var it = this.items[this.cur];
    if (!b || !it) return b;
    var of = this.cur + this.side + this.w + 'x' + this.h;
    if (of !== this.inkOf) {
      this.inkOf = of;
      var x0 = Infinity;
      var y0 = Infinity;
      var x1 = -Infinity;
      var y1 = -Infinity;
      for (var c = it.firstChild; c; c = c.nextSibling) {
        if (c.textContent === '\u00a0') continue;
        x0 = Math.min(x0, c.offsetLeft);
        y0 = Math.min(y0, c.offsetTop);
        x1 = Math.max(x1, c.offsetLeft + c.offsetWidth);
        y1 = Math.max(y1, c.offsetTop + c.offsetHeight);
      }
      this.inkBox = x0 < x1 ? [x0, y0, x1, y1] : null;
    }
    var k = this.inkBox;
    return k ? [b[0] + k[0], b[1] + k[1], b[0] + k[2], b[1] + k[3]] : b;
  };

  /* put the readout at spot p (or hide it without one) */
  Readout.prototype.move = function (p) {
    if (!p) {
      this.show(false);
      return;
    }
    this.o = p.o;
    if (p.side !== this.side) {
      this.side = p.side;
      var d = p.side.replace('f', '');
      this.el.classList.toggle('is-r', d === 'w' || d === 'nw' || d === 'sw' || d.charAt(2) === 'r');
      this.el.classList.toggle('is-c', d === 'n' || d === 's');
    }
    var t = 'translate3d(' + p.b[0] + 'px,' + p.b[1] + 'px,0)';
    if (t !== this.at) {
      this.at = t;
      this.el.style.transform = t;
    }
    this.show(true);
  };

  /* whether segment a-b crosses box [x0, y0, x1, y1] */
  function segBox(ax, ay, bx, by, x0, y0, x1, y1) {
    if ((ax < x0 && bx < x0) || (ax > x1 && bx > x1) || (ay < y0 && by < y0) || (ay > y1 && by > y1)) return false;
    if (ax >= x0 && ax <= x1 && ay >= y0 && ay <= y1) return true;
    var t0 = 0;
    var t1 = 1;
    var dx = bx - ax;
    var dy = by - ay;
    var p = [-dx, dx, -dy, dy];
    var q = [ax - x0, x1 - ax, ay - y0, y1 - ay];
    for (var i = 0; i < 4; i++) {
      if (p[i] === 0) {
        if (q[i] < 0) return false;
      } else {
        var t = q[i] / p[i];
        if (p[i] < 0) t0 = Math.max(t0, t);
        else t1 = Math.min(t1, t);
        if (t0 > t1) return false;
      }
    }
    return true;
  }

  var maps = 0;

  function init(root) {
    var view = root.querySelector('[data-shm-view]');
    var stage = root.querySelector('[data-shm-stage]');
    var svg = root.querySelector('[data-shm-svg]');
    var relief = root.querySelector('[data-shm-relief]');
    var hotPath = root.querySelector('[data-shm-hot]');
    var casePath = root.querySelector('[data-shm-case]');
    var climber = root.querySelector('[data-shm-climber]');
    var card = root.querySelector('[data-shm-card]');
    var fig = root.querySelector('[data-shm-fig]');
    var allBtn = root.querySelector('[data-shm-all]');
    var btn3d = root.querySelector('[data-shm-3d]');
    var copyBtn = root.querySelector('[data-shm-copy]');
    var over = root.querySelector('[data-shm-over]');
    if (!view || !stage || !svg || !card || !over) return;

    /* routes on the map and their entries in the list */
    var routes = {};
    var order = [];
    var els = root.querySelectorAll('.shm__r[data-k]');
    for (var i = 0; i < els.length; i++) {
      var r = new Route(els[i].getAttribute('data-k'), els[i]);
      if (r.xs.length < 2) continue;
      routes[r.key] = r;
      order.push(r);
    }
    var links = {};
    var meta = {};
    var ls = root.querySelectorAll('.shm__trip[data-k]');
    for (var j = 0; j < ls.length; j++) {
      var a = ls[j];
      var key = a.getAttribute('data-k');
      if (!routes[key]) continue;
      (links[key] = links[key] || []).push(a);
      if (!meta[key]) {
        meta[key] = {
          href: a.getAttribute('href'),
          type: a.getAttribute('data-type'),
          days: parseInt(a.getAttribute('data-days'), 10) || 0,
          title: a.getAttribute('data-title') || '',
          level: a.getAttribute('data-level') || '',
          high: a.getAttribute('data-high') || '',
          price: a.getAttribute('data-price') || '',
          next: a.getAttribute('data-next') || '',
          climb: a.getAttribute('data-climb') || '',
          ro: readStops(a.getAttribute('data-stops'))
        };
      }
    }
    /* a route without a trip in the store is not offered */
    order = order.filter(function (rt) {
      if (meta[rt.key]) return true;
      rt.el.style.display = 'none';
      delete routes[rt.key];
      return false;
    });
    if (!order.length) return;

    var ats = [];
    var atEls = root.querySelectorAll('[data-shm-over] .shm__at');
    for (var q = 0; q < atEls.length; q++) {
      ats.push({
        el: atEls[q],
        lab: atEls[q].querySelector('.shm__lab'),
        sk: atEls[q].querySelector('.shm__glyph'),
        mk: atEls[q].querySelector('.shm__glyph, .shm__dot'),
        box: null,
        mb: null,
        ys: null,
        nx: 0,
        hid: false,
        g: atEls[q].classList.contains('shm__pk') ? 13 : 0,
        under: false,
        x: +atEls[q].getAttribute('data-x'),
        y: +atEls[q].getAttribute('data-y')
      });
    }

    /* ---------- the camera: center (x, y) in map units and zoom z ---------- */
    var vw = view.clientWidth || 1;
    var vh = view.clientHeight || 1;
    var k = vw / VW;
    var cam = { x: VW / 2, y: VH / 2, z: 1 };
    var fly = null;
    var flyFrame = 0;

    function x0y0() {
      return { x: cam.x - VW / cam.z / 2, y: cam.y - VH / cam.z / 2 };
    }

    function toScreen(mx, my) {
      var o = x0y0();
      return { x: (mx - o.x) * k * cam.z, y: (my - o.y) * k * cam.z };
    }

    function clampCam(c) {
      var w = VW / c.z / 2;
      var h = VH / c.z / 2;
      c.x = Math.min(VW - w, Math.max(w, c.x));
      c.y = Math.min(VH - h, Math.max(h, c.y));
      return c;
    }

    /* "All of Nepal" shows once the map is off its whole view; the root
       says so for the credit, which then moves on narrow phones */
    function showAll(z) {
      if (!allBtn) return;
      allBtn.hidden = !(z > 1.02 || pin);
      root.classList.toggle('is-all', !allBtn.hidden);
    }

    function apply() {
      var o = x0y0();
      var z = cam.z;
      var home = z < 1.0001;
      svg.setAttribute('viewBox', home ? '0 0 1200 709' : o.x.toFixed(3) + ' ' + o.y.toFixed(3) + ' ' + (VW / z).toFixed(3) + ' ' + (VH / z).toFixed(3));
      svg.style.setProperty('--u', (1 / (k * z)).toFixed(4));
      stage.style.transform = home ? '' : 'translate3d(' + (-o.x * k * z).toFixed(2) + 'px,' + (-o.y * k * z).toFixed(2) + 'px,0) scale(' + z.toFixed(4) + ')';
      for (var i = 0; i < ats.length; i++) {
        var at = ats[i];
        at.el.style.transform = home ? '' : 'translate3d(' + ((at.x - o.x) * k * z - at.x * k).toFixed(2) + 'px,' + ((at.y - o.y) * k * z - at.y * k).toFixed(2) + 'px,0)';
      }
      showAll(z);
      if (!fly) placeLabels();
      roBusy = null;
      edges(o, z);
      root.classList.toggle('is-deep', z >= DEEP);
      paint();
      tiles();
    }

    function flyTo(target) {
      clampCam(target);
      if (flyFrame) cancelAnimationFrame(flyFrame);
      flyFrame = 0;
      if (reduced()) {
        cam = target;
        fly = null;
        apply();
        return;
      }
      fly = { t0: performance.now(), from: { x: cam.x, y: cam.y, z: cam.z }, to: target };
      flyFrame = requestAnimationFrame(flyTick);
    }

    function flyTick(now) {
      flyFrame = 0;
      if (!fly) return;
      var u = Math.min(1, (now - fly.t0) / FLY_MS);
      var e = ease(u);
      var a = fly.from;
      var b = fly.to;
      /* zoom on a log scale; the center moves so that the screen point
         under it travels evenly */
      var z = Math.exp(Math.log(a.z) + (Math.log(b.z) - Math.log(a.z)) * e);
      var w = a.z === b.z ? e : (1 / a.z - 1 / z) / (1 / a.z - 1 / b.z);
      cam = { x: a.x + (b.x - a.x) * w, y: a.y + (b.y - a.y) * w, z: z };
      if (u >= 1) fly = null;
      apply();
      if (fly) flyFrame = requestAnimationFrame(flyTick);
    }

    var PAD_X = 72;
    var PAD_Y = 52;

    /* the camera for route box b with the card over one side of the map.
       Near the map's edge the camera stops short, which can push the route
       under the card; zoom out until it clears */
    function place(b, side, onLeft, z, hid) {
      for (;;) {
        var s = k * z;
        var c = clampCam({
          x: (b[0] + b[2]) / 2 + (onLeft ? -side : side) / 2 / s,
          y: (b[1] + b[3]) / 2 + (hid / 2 - 10) / s,
          z: z
        });
        var x0 = c.x - VW / z / 2;
        var y0 = c.y - VH / z / 2;
        var lo = (onLeft ? side : 0) + PAD_X / 2;
        var hi = vw - (onLeft ? 0 : side) - PAD_X / 2;
        if (z <= 1 || ((b[0] - x0) * s >= lo && (b[2] - x0) * s <= hi && (b[3] - y0) * s <= vh - hid - PAD_Y / 2)) return c;
        z = Math.max(1, z * 0.94);
      }
    }

    /* how much of the bottom of the map is out of sight (under the sheet,
       or below the screen) once reveal() has scrolled the map into view:
       none where the map fits, some where the screen is short */
    function cover() {
      var h = fig.offsetHeight;
      var avail = window.innerHeight - (isWide() ? 0 : card.offsetHeight);
      var top = Math.max(64, (avail - h) / 2);
      return Math.max(0, Math.min(vh * 0.6, top + h - avail));
    }

    /* on wide screens the card sits over the right of the map, or over the
       left for routes by the east edge (Kanchenjunga) */
    function fit(rt) {
      var b = rt.box;
      var side = isWide() ? card.offsetWidth + 24 : 0;
      var hid = cover();
      var w = Math.max(8, b[2] - b[0]);
      var h = Math.max(8, b[3] - b[1]);
      var zmax = Math.min(6, 4 / k);
      var z = Math.max(1, Math.min(zmax, (vw - side - PAD_X * 2) / (w * k), (vh - hid - PAD_Y * 2 - 20) / (h * k)));
      var right = place(b, side, false, z, hid);
      var left = side ? place(b, side, true, z, hid) : right;
      if (left.z > right.z + 0.01) {
        left.left = true;
        return left;
      }
      return right;
    }

    function frame(key) {
      var target = fit(routes[key]);
      card.classList.toggle('is-left', !!target.left);
      return target;
    }

    /* ---------- sharper relief tiles while zoomed in ---------- */
    var tileBase = root.getAttribute('data-tiles') || '';
    var tileEls = {};

    function avif() {
      var src = (relief && (relief.currentSrc || relief.src)) || '';
      return /\.avif(\?|$)/.test(src);
    }

    function tiles() {
      var deep = cam.z >= DEEP;
      for (var id in tileEls) tileEls[id].classList.toggle('is-in', deep && tileEls[id].__ok);
      if (!deep || !tileBase || !avif()) return;
      var o = x0y0();
      var w = VW / cam.z;
      var h = VH / cam.z;
      var c0 = Math.max(0, Math.floor(o.x / TILE));
      var c1 = Math.min(TCOLS - 1, Math.floor((o.x + w - 0.01) / TILE));
      var r0 = Math.max(0, Math.floor(o.y / TILE));
      var r1 = Math.min(TROWS - 1, Math.floor((o.y + h - 0.01) / TILE));
      for (var c = c0; c <= c1; c++) {
        for (var r = r0; r <= r1; r++) {
          var key = c + '-' + r;
          if (tileEls[key]) continue;
          var img = document.createElement('img');
          img.className = 'shm__tile';
          img.alt = '';
          img.decoding = 'async';
          img.setAttribute('aria-hidden', 'true');
          img.style.left = ((c * TILE) / VW) * 100 + '%';
          img.style.top = ((r * TILE) / VH) * 100 + '%';
          img.style.width = (TILE / VW) * 100 + '%';
          img.style.height = (Math.min(TILE, VH - r * TILE) / VH) * 100 + '%';
          img.onload = onTile;
          tileEls[key] = img;
          stage.appendChild(img);
          img.src = tileBase + 'sh-nepal-t-' + c + '-' + r + '.avif';
        }
      }
    }

    function onTile(e) {
      var img = e.currentTarget;
      img.__ok = true;
      if (cam.z >= DEEP) img.classList.add('is-in');
    }

    /* ---------- the climber ---------- */
    var PER_PX = 5; /* ms per screen px walked */
    var MIN_MS = 450;
    var MAX_MS = 2400;
    var STEP_PX = 7;
    var STEP_MS = 90;
    var STOP_MS = 180;
    var walker = {
      rt: null,
      s: 0,
      target: 0,
      walk: null,
      then: -1,
      frame: 0,
      step: false,
      travel: 0,
      stepAt: 0,
      lastX: 0,
      lastY: 0,
      back: false
    };

    /* a label the climber stands on fades until it walks on. Label boxes
       are kept relative to their anchor, which only moves with the camera */
    var labsDirty = true;

    function measureLabels() {
      labsDirty = false;
      for (var i = 0; i < ats.length; i++) {
        var at = ats[i];
        if (!at.lab) continue;
        var a = at.el.getBoundingClientRect();
        var r = at.lab.getBoundingClientRect();
        var dx = r.left - a.left - at.nx;
        at.box = r.width ? [dx, r.top - a.top, dx + r.width, r.bottom - a.top] : null;
        var g = at.sk ? at.sk.getBoundingClientRect() : r;
        at.ys = [Math.min(r.top, g.top) - a.top, Math.max(r.bottom, g.bottom) - a.top];
        var m = at.mk ? at.mk.getBoundingClientRect() : null;
        at.mb = m && m.width ? [m.left - a.left, m.top - a.top, m.right - a.left, m.bottom - a.top] : null;
      }
    }

    /* a label whose point is near the side of the map would be cut off
       there; slide it back in, leaving its sketch on the point,
       unless that would put it on another label. A label that would
       still show only in part (blocked that way, its point past the side,
       beyond its peak sketch, or the label or sketch across the top or
       bottom) is hidden, and so is one the open trip card covers */
    function edges(o, z) {
      if (labsDirty) measureLabels();
      var cb = isWide() && card.classList.contains('is-open') ? [card.offsetLeft, card.offsetTop, card.offsetLeft + card.offsetWidth, card.offsetTop + card.offsetHeight] : null;
      var boxes = [];
      var i;
      for (i = 0; i < ats.length; i++) {
        var at = ats[i];
        var lb = at.box;
        at.want = 0;
        at.cut = false;
        if (!lb) continue;
        var sx = (at.x - o.x) * k * z;
        var sy = (at.y - o.y) * k * z;
        if (sx + lb[0] >= vw || sx + lb[2] <= 0 || sy + lb[1] >= vh || sy + lb[3] <= 0) continue;
        if (sx < -at.g || sx > vw + at.g || sy + at.ys[0] < -2 || sy + at.ys[1] > vh + 2) {
          at.cut = true;
          continue;
        }
        if (sx + lb[2] > vw - 4) at.want = vw - 4 - sx - lb[2];
        else if (sx + lb[0] < 4) at.want = 4 - sx - lb[0];
        boxes[i] = [sx + lb[0] + at.want, sy + lb[1], sx + lb[2] + at.want, sy + lb[3]];
      }
      for (i = 0; i < ats.length; i++) {
        var a = ats[i];
        var nx = a.want;
        for (var j = 0; nx && j < ats.length; j++) {
          var b = boxes[j];
          if (j !== i && b && boxes[i][0] < b[2] && b[0] < boxes[i][2] && boxes[i][1] < b[3] && b[1] < boxes[i][3]) nx = 0;
        }
        if (a.want && !nx) a.cut = true;
        var ab = boxes[i];
        if (cb && ab && ab[0] < cb[2] && cb[0] < ab[2] && ab[1] < cb[3] && cb[1] < ab[3]) a.cut = true;
        if (nx !== a.nx) {
          a.nx = nx;
          a.lab.style.left = nx ? nx.toFixed(1) + 'px' : '';
        }
        if (a.cut !== a.hid) {
          a.hid = a.cut;
          a.lab.style.visibility = a.cut ? 'hidden' : '';
        }
      }
    }

    /* a label under the climber fades, and one under its readout (box
       rb) goes, with its mark when the readout covers that too */
    function dodge(sp, rb) {
      if (sp && labsDirty) measureLabels();
      for (var i = 0; i < ats.length; i++) {
        var at = ats[i];
        var under = 0;
        if (sp && at.box) {
          var o = toScreen(at.x, at.y);
          var x0 = o.x + at.box[0];
          var x1 = o.x + at.box[2];
          var y0 = o.y + at.box[1];
          var y1 = o.y + at.box[3];
          var m = at.mb;
          if (rb && rb[0] < x1 && rb[2] > x0 && rb[1] < y1 && rb[3] > y0) under = 2;
          else if (rb && m && rb[0] < o.x + m[2] && rb[2] > o.x + m[0] && rb[1] < o.y + m[3] && rb[3] > o.y + m[1]) under = 2;
          else if (sp.x + 10 > x0 && sp.x - 10 < x1 && sp.y > y0 && sp.y - 26 < y1) under = 1;
        }
        if (under !== at.under) {
          at.under = under;
          at.el.classList.toggle('is-under', under === 1);
          at.el.classList.toggle('is-ro', under === 2);
        }
      }
    }

    function paint() {
      if (!walker.rt) return null;
      var p = walker.rt.at(walker.s);
      var sp = toScreen(p.x, p.y);
      climber.style.transform = 'translate3d(' + sp.x.toFixed(2) + 'px,' + sp.y.toFixed(2) + 'px,0)';
      dodge(sp, roPaint(sp));
      return sp;
    }

    /* ---------- the readout beside the climber ---------- */
    var readout = new Readout(over);
    var mqPhone = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
    var roBusy = null; /* found again when the camera, the card or the size changes */
    var roMarks = [btn3d, allBtn, root.querySelector('.shm__credit summary')];

    /* what the readout keeps off: the trip card where it settles (over
       the side of the map on wide screens; on phones the part of the map
       the sheet covers) and the map's buttons and credit */
    function busyBoxes() {
      var out = [];
      if (card.classList.contains('is-open')) {
        if (isWide()) {
          out.push([card.offsetLeft - 6, card.offsetTop - 6, card.offsetLeft + card.offsetWidth + 6, card.offsetTop + card.offsetHeight + 6]);
        } else {
          var hid = cover();
          if (hid > 0) out.push([-10, vh - hid - 6, vw + 10, vh + 10]);
        }
      }
      var vr = view.getBoundingClientRect();
      for (var i = 0; i < roMarks.length; i++) {
        var el = roMarks[i];
        if (!el || el.hidden || !el.offsetWidth) continue;
        var r = el.getBoundingClientRect();
        if (r.bottom > vr.top && r.top < vr.bottom && r.right > vr.left && r.left < vr.right) out.push([r.left - vr.left - 4, r.top - vr.top - 4, r.right - vr.left + 4, r.bottom - vr.top + 4]);
      }
      return out;
    }

    /* the border, for the readout here and the labels below */
    var land = root.querySelector('.shm__land');
    var edge = land ? new Route('', land) : null;

    /* whether route rt runs through box b (screen px) */
    function pathIn(rt, b) {
      var o = x0y0();
      var sc = k * cam.z;
      var x0 = o.x + b[0] / sc;
      var y0 = o.y + b[1] / sc;
      var x1 = o.x + b[2] / sc;
      var y1 = o.y + b[3] / sc;
      var bx = rt.box;
      if (x1 < bx[0] || x0 > bx[2] || y1 < bx[1] || y0 > bx[3]) return false;
      for (var i = 1; i < rt.xs.length; i++) {
        if (!rt.jump[i] && segBox(rt.xs[i - 1], rt.ys[i - 1], rt.xs[i], rt.ys[i], x0, y0, x1, y1)) return true;
      }
      return false;
    }

    function lineIn(b) {
      return pathIn(walker.rt, b);
    }

    function edgeIn(b) {
      return !!edge && pathIn(edge, b);
    }

    /* whether box b (screen px) covers a peak's sketch or a town's dot */
    function markIn(b) {
      for (var i = 0; i < ats.length; i++) {
        var m = ats[i].mb;
        if (!m) continue;
        var o = toScreen(ats[i].x, ats[i].y);
        if (b[0] < o.x + m[2] && b[2] > o.x + m[0] && b[1] < o.y + m[3] && b[3] > o.y + m[1]) return true;
      }
      return false;
    }

    /* what the readout keeps off where it can: the border, and the marks */
    function softIn(b) {
      return edgeIn(b) || markIn(b);
    }

    /* the readout for the climber's feet at sp; its box, or null */
    function roPaint(sp) {
      var i = stopAt(walker.rt, readout.ro, walker.s);
      if (i < 0) {
        readout.show(false);
        return null;
      }
      readout.pick(i);
      readout.size();
      if (!roBusy) roBusy = busyBoxes();
      if (labsDirty) measureLabels();
      var cb = mqPhone && mqPhone.matches ? [-9, -22, 9, 0] : [-11, -27, 11, 0];
      var p = roSpot(readout, sp.x, sp.y, cb, vw, vh, roBusy, lineIn, softIn);
      readout.move(p);
      return readout.ink(p && p.b);
    }

    function sample(t) {
      var w = walker.walk;
      var u = (t - w.t0) / w.d;
      if (u >= 1) return { s: w.p1, v: 0, done: true };
      if (u < 0) u = 0;
      var u2 = u * u;
      var u3 = u2 * u;
      var pos = (2 * u3 - 3 * u2 + 1) * w.p0 + (u3 - 2 * u2 + u) * w.m0 + (-2 * u3 + 3 * u2) * w.p1;
      var dv = (6 * u2 - 6 * u) * w.p0 + (3 * u2 - 4 * u + 1) * w.m0 + (-6 * u2 + 6 * u) * w.p1;
      return { s: pos, v: dv / w.d, done: false };
    }

    function setStep(on) {
      if (on === walker.step) return;
      walker.step = on;
      climber.classList.toggle('is-step', on);
    }

    function face(dx) {
      if (Math.abs(dx) < 0.25) return;
      var back = dx < 0;
      if (back === walker.back) return;
      walker.back = back;
      climber.classList.toggle('is-back', back);
    }

    function walkTick(now) {
      walker.frame = 0;
      if (!walker.walk) return;
      var at = sample(now);
      walker.s = at.s;
      var p = paint();
      var dx = p.x - walker.lastX;
      face(dx);
      walker.travel += Math.abs(dx) + Math.abs(p.y - walker.lastY);
      walker.lastX = p.x;
      walker.lastY = p.y;
      if (walker.travel >= STEP_PX && now - walker.stepAt >= STEP_MS) {
        walker.travel = 0;
        walker.stepAt = now;
        setStep(!walker.step);
      }
      if (at.done) {
        walker.walk = null;
        if (walker.then < 0) {
          setStep(false);
          return;
        }
        var next = walker.then;
        walker.then = -1;
        plan(next, now, 0, 0);
        return;
      }
      walker.frame = requestAnimationFrame(walkTick);
    }

    function plan(target, now, v0, minMs) {
      var delta = target - walker.s;
      var d = Math.min(MAX_MS, Math.max(minMs || MIN_MS, PER_PX * Math.abs(delta) * k * cam.z));
      var m0 = 0;
      if (v0 * delta > 0) {
        d = Math.min(d, (3 * delta) / v0);
        m0 = v0 * d;
      }
      walker.walk = { t0: now, d: d, p0: walker.s, p1: target, m0: m0 };
      if (!walker.frame) walker.frame = requestAnimationFrame(walkTick);
    }

    /* send the climber to distance s on its route; a new target mid-walk
       takes over from the current position and speed, and a target behind
       it is reached after easing to a stop and turning, as in the charts */
    function walkTo(s, minMs) {
      var rt = walker.rt;
      if (!rt) return;
      s = Math.max(0, Math.min(rt.len, s));
      if (Math.abs(s - walker.target) < 0.01 && (walker.walk || Math.abs(walker.s - s) < 0.01)) return;
      var now = performance.now();
      var v0 = 0;
      if (walker.walk) {
        var at = sample(now);
        walker.s = at.s;
        v0 = at.done ? 0 : at.v;
      }
      walker.target = s;
      walker.then = -1;
      var delta = s - walker.s;
      if (reduced() || Math.abs(delta) < 0.001) {
        if (walker.frame) cancelAnimationFrame(walker.frame);
        walker.frame = 0;
        walker.walk = null;
        var from = rt.at(walker.s);
        walker.s = s;
        var to = rt.at(s);
        face(to.x - from.x);
        setStep(false);
        paint();
        return;
      }
      var sp = toScreen(rt.at(walker.s).x, rt.at(walker.s).y);
      walker.lastX = sp.x;
      walker.lastY = sp.y;
      if (v0 * delta < 0) {
        var t = STOP_MS;
        var run = (v0 * t) / 2;
        var edge = v0 > 0 ? rt.len - walker.s : -walker.s;
        if (Math.abs(run) > Math.abs(edge)) {
          run = edge;
          t = (2 * run) / v0;
        }
        if (Math.abs(run) > 0.001) {
          walker.walk = { t0: now, d: t, p0: walker.s, p1: walker.s + run, m0: v0 * t };
          walker.then = s;
          if (!walker.frame) walker.frame = requestAnimationFrame(walkTick);
          return;
        }
        v0 = 0;
      }
      plan(s, now, v0, minMs);
    }

    /* put the climber at the start of a route and send it on */
    function startWalk(rt, s) {
      if (walker.frame) cancelAnimationFrame(walker.frame);
      walker.frame = 0;
      walker.walk = null;
      walker.then = -1;
      walker.rt = rt;
      walker.s = 0;
      walker.target = -1;
      setStep(false);
      var a = rt.at(0);
      var b = rt.at(Math.min(rt.len, 2));
      walker.back = !(b.x - a.x < 0);
      face(b.x - a.x || 1);
      paint();
      climber.classList.add('is-on');
      walkTo(s, 900);
    }

    /* ---------- what is shown: a pinned route, or one pointed at ---------- */
    var pin = null; /* clicked, tapped or linked */
    var hover = null; /* pointed at on the map or in the list, or focused */
    var shown = null;
    var filt = { type: '', len: '' };

    function lengthOf(days) {
      if (!days) return '';
      if (days < 14) return 'short';
      if (days <= 21) return 'mid';
      return 'long';
    }

    function matches(key) {
      var m = meta[key];
      if (!m) return false;
      if (filt.type && m.type !== filt.type) return false;
      if (filt.len && lengthOf(m.days) !== filt.len) return false;
      return true;
    }

    var fields = {};
    var rows = {};
    var fe = card.querySelectorAll('[data-c]');
    for (var f = 0; f < fe.length; f++) fields[fe[f].getAttribute('data-c')] = fe[f];
    var re = card.querySelectorAll('[data-row]');
    for (var g = 0; g < re.length; g++) rows[re[g].getAttribute('data-row')] = re[g];

    function fill(key) {
      var m = meta[key];
      fields.title.textContent = m.title;
      var vals = { days: m.days ? String(m.days) : '', level: m.level, high: m.high, price: m.price, next: m.next };
      for (var n in vals) {
        if (!fields[n]) continue;
        fields[n].textContent = vals[n];
        if (rows[n]) rows[n].hidden = !vals[n];
      }
      fields.href.setAttribute('href', m.href);
      if (copyBtn) copyBtn.textContent = 'Copy link';
    }

    function show() {
      var key = hover || pin;
      if (key === shown) return;
      var old = shown;
      shown = key;
      roBusy = null;
      if (old && links[old]) links[old].forEach(function (a) { a.classList.remove('is-hot'); });
      root.classList.toggle('is-on', !!key);
      if (!key) {
        hotPath.setAttribute('d', '');
        casePath.setAttribute('d', '');
        card.classList.remove('is-open');
        edges(x0y0(), cam.z);
        climber.classList.remove('is-on');
        if (walker.frame) cancelAnimationFrame(walker.frame);
        walker.frame = 0;
        walker.walk = null;
        walker.rt = null;
        readout.show(false);
        dodge(null);
        return;
      }
      var rt = routes[key];
      var d = rt.el.getAttribute('d');
      hotPath.setAttribute('d', d);
      casePath.setAttribute('d', d);
      if (links[key]) links[key].forEach(function (a) { a.classList.add('is-hot'); });
      fill(key);
      readout.set(meta[key].ro);
      /* a preview opens on the right; only a pinned route moves the card */
      if (!old && !pin) card.classList.remove('is-left');
      card.classList.add('is-open');
      edges(x0y0(), cam.z);
    }

    function setHover(key, s) {
      var was = shown;
      hover = key;
      show();
      if (!shown) return;
      if (shown !== was || !walker.rt) startWalk(routes[shown], s == null ? routes[shown].goal : s);
      else if (s != null) walkTo(s);
    }

    function setUrl(key) {
      if (!window.history || !history.replaceState || !window.URL) return;
      try {
        var u = new URL(window.location.href);
        if (key) u.searchParams.set('route', key);
        else u.searchParams.delete('route');
        history.replaceState(history.state, '', u.toString());
      } catch (e) {}
    }

    function setPin(key, s, instant) {
      pin = key;
      hover = null;
      var was = shown;
      show();
      setUrl(key);
      if (key) {
        if (shown !== was || !walker.rt) startWalk(routes[key], s == null ? routes[key].goal : s);
        else if (s != null) walkTo(s);
        var target = frame(key);
        if (instant) {
          cam = clampCam(target);
          apply();
        } else {
          flyTo(target);
        }
      } else {
        flyTo({ x: VW / 2, y: VH / 2, z: 1 });
      }
      showAll(cam.z);
    }

    /* ---------- pointing at the map ---------- */
    var rect = null;
    var pending = null;
    var moveFrame = 0;
    var leaveTimer = 0;
    var lastPX = -1;
    var lastPY = -1;
    var downType = 'mouse';

    function hit(cx, cy, tol) {
      rect = view.getBoundingClientRect();
      var o = x0y0();
      var sc = k * cam.z;
      var mx = o.x + (cx - rect.left) / sc;
      var my = o.y + (cy - rect.top) / sc;
      var best = null;
      var bd = Infinity;
      for (var i = 0; i < order.length; i++) {
        var rt = order[i];
        if (!matches(rt.key)) continue;
        var b = rt.box;
        var pad = tol / sc;
        if (mx < b[0] - pad || mx > b[2] + pad || my < b[1] - pad || my > b[3] + pad) continue;
        var n = rt.near(mx, my);
        var d = Math.sqrt(n.d2) * sc;
        /* on shared trails the route already shown keeps the pointer,
           then the region's main trip wins over the others */
        if (rt.key === shown) d -= 5;
        d += Math.min(4, rt.rank * 0.4);
        if (d < bd) {
          bd = d;
          best = { key: rt.key, s: n.s };
        }
      }
      return bd <= tol ? best : null;
    }

    function onMoveFrame() {
      moveFrame = 0;
      if (!pending) return;
      var e = pending;
      pending = null;
      var h = hit(e.x, e.y, 12);
      view.style.cursor = h ? 'pointer' : '';
      if (h) setHover(h.key, h.s);
    }

    view.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch' || drag) return;
      /* a page scrolling under a resting pointer sends moves at the same
         screen point; only a pointer that moves counts */
      if (e.clientX === lastPX && e.clientY === lastPY) return;
      lastPX = e.clientX;
      lastPY = e.clientY;
      if (e.target.closest && e.target.closest('[data-shm-card], .shm__ctl')) return;
      clearTimeout(leaveTimer);
      pending = { x: e.clientX, y: e.clientY };
      if (!moveFrame) moveFrame = requestAnimationFrame(onMoveFrame);
    });

    view.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'touch') return;
      lastPX = -1;
      clearTimeout(leaveTimer);
      leaveTimer = setTimeout(function () {
        if (hover) {
          hover = null;
          var was = shown;
          show();
          if (shown && shown !== was) startWalk(routes[shown], routes[shown].goal);
        }
      }, 300);
    });

    view.addEventListener('pointerenter', function () {
      clearTimeout(leaveTimer);
    });

    view.addEventListener('pointerdown', function (e) {
      downType = e.pointerType || 'mouse';
    });

    view.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-shm-card], .shm__ctl')) return;
      /* the click that ends a drag of the climber */
      if (performance.now() - dragEnd < 400) return;
      var touch = downType === 'touch';
      var h = hit(e.clientX, e.clientY, touch ? 26 : 12);
      if (h) {
        if (h.key === pin) {
          walkTo(h.s);
        } else {
          setPin(h.key, touch ? null : h.s);
          if (touch) reveal();
        }
      } else if (pin || cam.z > 1.02) {
        setPin(null);
      }
    });

    /* on touch screens the climber can be dragged along its route: it
       walks after the finger, keeping where the finger took hold of it */
    var grab = document.createElement('span');
    grab.className = 'shm__grab';
    climber.appendChild(grab);
    var drag = null;
    var dragEnd = 0;
    grab.addEventListener('pointerdown', function (e) {
      if (!walker.rt || e.button > 0) return;
      var r = view.getBoundingClientRect();
      var p = walker.rt.at(walker.s);
      var sp = toScreen(p.x, p.y);
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: sp.x - (e.clientX - r.left), dy: sp.y - (e.clientY - r.top), moved: false };
      try {
        grab.setPointerCapture(e.pointerId);
      } catch (err) {}
    });
    grab.addEventListener('pointermove', function (e) {
      if (!drag || e.pointerId !== drag.id || !walker.rt) return;
      if (!drag.moved && Math.abs(e.clientX - drag.x) + Math.abs(e.clientY - drag.y) < 6) return;
      drag.moved = true;
      var r = view.getBoundingClientRect();
      var o = x0y0();
      var sc = k * cam.z;
      var n = walker.rt.near(o.x + (e.clientX - r.left + drag.dx) / sc, o.y + (e.clientY - r.top + drag.dy) / sc);
      walkTo(n.s);
    });
    function endDrag(e) {
      if (!drag || e.pointerId !== drag.id) return;
      if (drag.moved) dragEnd = performance.now();
      drag = null;
    }
    grab.addEventListener('pointerup', endDrag);
    grab.addEventListener('pointercancel', endDrag);
    grab.addEventListener('lostpointercapture', endDrag);

    /* ---------- the list ---------- */
    var listDown = 'mouse';
    var backTo = null; /* where the list was, to return to on close */
    var muteFocus = false;

    /* the arrow keys on a trip in the list walk the climber from stop to
       stop, and the stop is read out */
    var sayEl = document.createElement('p');
    sayEl.className = 'shm__sr';
    sayEl.setAttribute('aria-live', 'polite');
    fig.appendChild(sayEl);
    var keysEl = document.createElement('p');
    keysEl.className = 'shm__sr';
    keysEl.id = 'shm-keys-' + ++maps;
    keysEl.textContent = 'Left and right arrow keys walk the climber from stop to stop on the map.';
    fig.appendChild(keysEl);

    function step(key, dir) {
      if (shown !== key) setHover(key);
      if (shown !== key || !walker.rt) return;
      var st = nextStop(meta[key].ro, walker.target >= 0 ? walker.target : walker.s, dir);
      if (!st) return;
      walkTo(st.s);
      sayEl.textContent = meta[key].ro.list[st.i].say;
    }

    function bindLink(a) {
      var key = a.getAttribute('data-k');
      if (meta[key].ro) {
        a.setAttribute('aria-keyshortcuts', 'ArrowLeft ArrowRight');
        a.setAttribute('aria-describedby', keysEl.id);
        a.addEventListener('keydown', function (e) {
          if ((e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || !matches(key)) return;
          e.preventDefault();
          step(key, e.key === 'ArrowRight' ? 1 : -1);
        });
      }
      a.addEventListener('pointerdown', function (e) {
        listDown = e.pointerType || 'mouse';
      });
      a.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'touch' || !matches(key)) return;
        setHover(key);
      });
      a.addEventListener('pointerleave', function (e) {
        if (e.pointerType === 'touch') return;
        if (hover === key) setHover(null);
      });
      a.addEventListener('focus', function () {
        if (muteFocus || !matches(key)) return;
        var visible = true;
        try {
          visible = a.matches(':focus-visible');
        } catch (err) {}
        if (visible) setHover(key);
      });
      a.addEventListener('blur', function () {
        if (hover === key) setHover(null);
      });
      /* on a touch screen the first tap shows the trip on the map; tapping
         it again, or "See this trip" in the card, opens the trip */
      a.addEventListener('click', function (e) {
        if (listDown !== 'touch' || pin === key || !matches(key)) return;
        e.preventDefault();
        listDown = 'mouse';
        if (backTo == null) backTo = window.pageYOffset;
        setPin(key);
        reveal();
      });
    }
    for (var lk in links) links[lk].forEach(bindLink);

    /* bring the map and its buttons into view above the sheet, with the
       top of the screen between lines of text (the heading, the filters)
       rather than through one. Scrolling up brings back the site header,
       which covers the top 64px */
    var texts = root.querySelectorAll('.shm__h, .shm__f');
    function reveal(instant) {
      var r = fig.getBoundingClientRect();
      var sheet = isWide() ? 0 : card.offsetHeight;
      var avail = window.innerHeight - sheet;
      var h = fig.offsetHeight;
      var top = Math.max(64, (avail - h) / 2);
      if (!instant && r.top >= top - 8 && r.top + h <= avail) return;
      var edge = r.top < top ? 64 : 0;
      var lo = edge + 8;
      var hi = Math.max(lo, avail - h);
      var pick = Math.min(hi, Math.max(lo, top));
      var lines = [];
      var tops = [pick];
      var i;
      for (i = 0; i < texts.length; i++) {
        var b = texts[i].getBoundingClientRect();
        if (!b.height) continue;
        lines.push([b.top - r.top, b.bottom - r.top]);
        tops.push(edge + 8 - (b.top - r.top), edge - 4 - (b.bottom - r.top));
      }
      var best = pick;
      var off = Infinity;
      for (i = 0; i < tops.length; i++) {
        var t = tops[i];
        var clear = t >= lo && t <= hi;
        for (var j = 0; clear && j < lines.length; j++) {
          if (t + lines[j][0] < edge && t + lines[j][1] > edge) clear = false;
        }
        if (clear && Math.abs(t - pick) < off) {
          best = t;
          off = Math.abs(t - pick);
        }
      }
      var y = window.pageYOffset + r.top - best;
      try {
        window.scrollTo({ top: y, behavior: instant || reduced() ? 'auto' : 'smooth' });
      } catch (err) {
        window.scrollTo(0, y);
      }
    }

    /* ---------- card controls ---------- */
    function close() {
      var key = pin || hover;
      var inCard = card.contains(document.activeElement);
      hover = null;
      setPin(null);
      if (inCard && key && links[key]) {
        muteFocus = true;
        links[key][0].focus({ preventScroll: true });
        muteFocus = false;
      }
      if (backTo != null) {
        var y = backTo;
        backTo = null;
        try {
          window.scrollTo({ top: y, behavior: reduced() ? 'auto' : 'smooth' });
        } catch (err) {
          window.scrollTo(0, y);
        }
      }
    }

    var closeBtn = card.querySelector('[data-shm-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);

    if (allBtn) {
      allBtn.addEventListener('click', function () {
        backTo = null;
        close();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && (pin || hover)) close();
    });

    function copyText(text, done) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, function () {
          legacyCopy(text) && done();
        });
      } else if (legacyCopy(text)) {
        done();
      }
    }

    function legacyCopy(text) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      var ok = false;
      try {
        ok = document.execCommand('copy');
      } catch (err) {}
      document.body.removeChild(ta);
      return ok;
    }

    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        if (!shown) return;
        var url = window.location.origin + window.location.pathname + '?route=' + encodeURIComponent(shown);
        copyText(url, function () {
          copyBtn.textContent = 'Link copied';
        });
      });
    }

    /* ---------- filters ---------- */
    var fbtns = root.querySelectorAll('[data-shm-filters] [data-f]');
    var inGroup = root.querySelector('[data-shm-routes]');
    var outGroup = root.querySelector('[data-shm-out]');

    function applyFilters() {
      for (var i = 0; i < order.length; i++) {
        var key = order[i].key;
        var out = !matches(key);
        order[i].el.classList.toggle('is-out', out);
        /* in drawing order, into the faded group or back */
        (out ? outGroup : inGroup).appendChild(order[i].el);
        if (links[key]) links[key].forEach(function (a) { a.classList.toggle('is-out', out); });
      }
      if (hover && !matches(hover)) hover = null;
      if (pin && !matches(pin)) setPin(null);
      else show();
    }

    for (var b = 0; b < fbtns.length; b++) {
      fbtns[b].addEventListener('click', function (e) {
        var btn = e.currentTarget;
        var group = btn.getAttribute('data-f');
        var v = btn.getAttribute('data-v');
        filt[group] = filt[group] === v ? '' : v;
        for (var i = 0; i < fbtns.length; i++) {
          if (fbtns[i].getAttribute('data-f') !== group) continue;
          fbtns[i].setAttribute('aria-pressed', String(filt[group] === fbtns[i].getAttribute('data-v')));
        }
        applyFilters();
      });
    }

    /* ---------- 3D, only where WebGL works, loaded on tap ---------- */
    var src3d = root.getAttribute('data-3d-src');
    var open3d = null;
    if (btn3d && src3d && webgl()) {
      btn3d.hidden = false;
      /* no disabled state while it loads: that would drop keyboard focus */
      var loading3d = false;
      /* auto: opened by a link as the page loads, not by a tap */
      open3d = function (auto) {
        if (loading3d) return;
        loading3d = true;
        load3d()
          .then(function (m) {
            loading3d = false;
            m.open(root, {
              route: shown || pin,
              routes: routes,
              meta: meta,
              keys: order.map(function (r) { return r.key; }).filter(matches),
              auto: auto === true,
              readout: { make: function (el) { return new Readout(el); }, at: stopAt, next: nextStop, spot: roSpot, seg: segBox }
            });
          })
          .catch(function () {
            loading3d = false;
            btn3d.hidden = true;
          });
      };
      btn3d.addEventListener('click', open3d);
      /* back from 3D: keep the route followed there, focus the button */
      root.addEventListener('shm3d:close', function (e) {
        var key = e.detail && e.detail.route;
        if (key && routes[key] && key !== pin) setPin(key, null, true);
        btn3d.focus({ preventScroll: true });
      });
    }

    /* a module script tag rather than import(): Shopify minifies theme
       scripts and turns import() in a classic script into require() */
    function load3d() {
      return new Promise(function (resolve, reject) {
        if (window.shNepalMap3d) {
          resolve(window.shNepalMap3d);
          return;
        }
        var s = document.createElement('script');
        s.type = 'module';
        s.src = src3d;
        s.onload = function () {
          if (window.shNepalMap3d) resolve(window.shNepalMap3d);
          else reject(new Error('3D module did not start'));
        };
        s.onerror = function () {
          s.remove();
          reject(new Error('3D module did not load'));
        };
        document.head.appendChild(s);
      });
    }

    function webgl() {
      try {
        var c = document.createElement('canvas');
        /* three.js draws with WebGL 2 only */
        var gl = c.getContext('webgl2');
        if (!gl) return false;
        var lose = gl.getExtension('WEBGL_lose_context');
        if (lose) lose.loseContext();
        return true;
      } catch (err) {
        return false;
      }
    }

    /* ---------- label placement ----------
       Each label takes the best free spot close to its own mark (a peak
       sketch or a town dot) on the map as it is shown: never on another
       label, a mark or the map's buttons, never past the map's edge, off
       the route lines and the border, and nearer its own mark than any
       other, so it reads as that mark's name. Labels side by side keep a
       wide gap, so two never read as one line. A peak label that has to
       sit more than about a letter's height from its sketch gets a
       hairline to it (a town's, a little further), which crosses a route
       or the border at most once where it can. Peaks go first, the one
       with the fewest free spots first, then towns, then regions. A town
       or region with no free spot for its name is left out; a peak label
       with none takes the spot that crosses the least route. Spots are
       found again on resize, when fonts arrive and when the camera comes
       to rest. Only transforms move, so the page never shifts. */
    var CELL = 2;
    var labsMeasured = false;

    /* where route (or border) lines run on the view, in 2 px cells, summed
       from the top left so the count in any box takes four lookups */
    function inkMap(rts, o, s) {
      var cw = Math.ceil(vw / CELL) + 1;
      var ch = Math.ceil(vh / CELL) + 1;
      var on = new Uint8Array(cw * ch);
      for (var r = 0; r < rts.length; r++) {
        var xs = rts[r].xs;
        var ys = rts[r].ys;
        for (var i = 1; i < xs.length; i++) {
          if (rts[r].jump[i]) continue;
          var ax = (xs[i - 1] - o.x) * s;
          var ay = (ys[i - 1] - o.y) * s;
          var dx = (xs[i] - xs[i - 1]) * s;
          var dy = (ys[i] - ys[i - 1]) * s;
          if ((ax < -8 && ax + dx < -8) || (ax > vw + 8 && ax + dx > vw + 8) || (ay < -8 && ay + dy < -8) || (ay > vh + 8 && ay + dy > vh + 8)) continue;
          var n = Math.max(1, Math.ceil(Math.sqrt(dx * dx + dy * dy)));
          for (var j = 0; j <= n; j++) {
            var cx = Math.floor((ax + (dx * j) / n) / CELL);
            var cy = Math.floor((ay + (dy * j) / n) / CELL);
            if (cx >= 0 && cy >= 0 && cx < cw && cy < ch) on[cy * cw + cx] = 1;
          }
        }
      }
      var w = cw + 1;
      var sum = new Int32Array(w * (ch + 1));
      for (var y = 0; y < ch; y++) {
        var row = 0;
        for (var x = 0; x < cw; x++) {
          row += on[y * cw + x];
          sum[(y + 1) * w + x + 1] = sum[y * w + x + 1] + row;
        }
      }
      return { sum: sum, w: w, cw: cw, ch: ch };
    }

    /* cells with a line in box b (screen px, grown by pad) */
    function inkIn(m, b, pad) {
      var x0 = Math.max(0, Math.floor((b[0] - pad) / CELL));
      var y0 = Math.max(0, Math.floor((b[1] - pad) / CELL));
      var x1 = Math.min(m.cw, Math.ceil((b[2] + pad) / CELL));
      var y1 = Math.min(m.ch, Math.ceil((b[3] + pad) / CELL));
      if (x1 <= x0 || y1 <= y0) return 0;
      var S = m.sum;
      var w = m.w;
      return S[y1 * w + x1] - S[y0 * w + x1] - S[y1 * w + x0] + S[y0 * w + x0];
    }

    /* how a line from [x0, y0] to [x1, y1] meets the ink within 3 px of
       it: 0 where it keeps clear, 1 where it crosses a line once, 2 where
       it runs over or beside one, or crosses more than once. It ends at a
       mark that may sit on a line: a line there counts only where it runs
       on along it past its last skip px */
    function inkOn(m, l, skip) {
      var dx = l[2] - l[0];
      var dy = l[3] - l[1];
      var len = Math.sqrt(dx * dx + dy * dy);
      var n = Math.ceil(len / CELL);
      var runs = 0;
      var run = 0;
      var most = 0;
      for (var i = 0; i <= n; i++) {
        var x = l[0] + (dx * i) / n;
        var y = l[1] + (dy * i) / n;
        if (inkIn(m, [x - 3, y - 3, x + 3, y + 3], 0)) {
          if (!run) runs++;
          run++;
        } else {
          most = Math.max(most, run);
          run = 0;
        }
      }
      if (run) {
        if (run * (len / n) - skip > 4) return 2;
        runs--;
      }
      return !runs ? 0 : runs === 1 && most * CELL <= 12 ? 1 : 2;
    }

    function hits(a, b, px, py) {
      return a[0] < b[2] + px && b[0] < a[2] + px && a[1] < b[3] + py && b[1] < a[3] + py;
    }

    /* the distance between two boxes, 0 where they touch */
    function gap(a, b) {
      var gx = Math.max(b[0] - a[2], a[0] - b[2], 0);
      var gy = Math.max(b[1] - a[3], a[1] - b[3], 0);
      return Math.sqrt(gx * gx + gy * gy);
    }

    /* the distance from point (x, y) to box b */
    function toBox(x, y, b) {
      var gx = Math.max(b[0] - x, x - b[2], 0);
      var gy = Math.max(b[1] - y, y - b[3], 0);
      return Math.sqrt(gx * gx + gy * gy);
    }

    /* whether the line l ([x1, y1, x2, y2]) runs through box b */
    function cuts(l, b) {
      var t0 = 0;
      var t1 = 1;
      var dx = l[2] - l[0];
      var dy = l[3] - l[1];
      var p = [-dx, dx, -dy, dy];
      var q = [l[0] - b[0], b[2] - l[0], l[1] - b[1], b[3] - l[1]];
      for (var i = 0; i < 4; i++) {
        if (p[i] === 0) {
          if (q[i] < 0) return false;
        } else {
          var t = q[i] / p[i];
          if (p[i] < 0) t0 = Math.max(t0, t);
          else t1 = Math.min(t1, t);
          if (t0 > t1) return false;
        }
      }
      return true;
    }

    /* whether two lines cross */
    function crosses(a, b) {
      function side(x, y, l) {
        return (l[2] - l[0]) * (y - l[1]) - (l[3] - l[1]) * (x - l[0]);
      }
      return side(a[0], a[1], b) * side(a[2], a[3], b) < 0 && side(b[0], b[1], a) * side(b[2], b[3], a) < 0;
    }

    /* the hairline from label box b to its mark, in screen px: to the
       summit from above, to the foot of the sketch from below, to the
       side of it or to a town's dot otherwise; short of both ends */
    function leadFor(at, b) {
      var f = at.f;
      var ax = at.sx;
      var ay = at.sy;
      var fw = f[2] - f[0];
      var fh = f[3] - f[1];
      var cx = (b[0] + b[2]) / 2;
      var cy = (b[1] + b[3]) / 2;
      var tx;
      var ty;
      if (!at.g) {
        tx = ax + (f[0] + f[2]) / 2;
        ty = ay + (f[1] + f[3]) / 2;
      } else if (b[3] <= ay + f[1] + fh * 0.4) {
        tx = ax;
        ty = ay;
      } else if (b[1] >= ay + f[3] + at.em * 0.5 || (b[1] >= ay + f[1] + fh * 0.6 && b[0] < ax + f[2] && b[2] > ax + f[0])) {
        tx = Math.min(ax + f[2] - fw * 0.3, Math.max(ax + f[0] + fw * 0.3, cx));
        ty = ay + f[3];
      } else {
        /* the flank at half height, so a label lower down gets a slanted
           line rather than one that reads as a dash before the name */
        tx = cx < ax + (f[0] + f[2]) / 2 ? ax + f[0] + fw * 0.2 : ax + f[2] - fw * 0.2;
        ty = ay + f[1] + fh * 0.5;
      }
      var px = Math.min(b[2], Math.max(b[0], tx));
      var py = Math.min(b[3], Math.max(b[1], ty));
      var dx = tx - px;
      var dy = ty - py;
      var d = Math.sqrt(dx * dx + dy * dy);
      /* a shorter line reads as a stray mark */
      if (d < 17) return null;
      var ux = dx / d;
      var uy = dy / d;
      var end = at.g ? 3 : 5;
      return [px + ux * 4, py + uy * 4, tx - ux * end, ty - uy * end];
    }

    /* each label's size, its mark and the spot the stylesheet gives it
       (its home, which the search prefers), measured with the camera
       anywhere since they are relative to the label's point */
    function measureHomes() {
      var i;
      var at;
      for (i = 0; i < ats.length; i++) {
        at = ats[i];
        at.el.style.display = '';
        if (!at.lab) continue;
        at.lab.style.display = '';
        at.lab.style.translate = '';
        at.lab.style.transform = '';
        at.lab.style.alignItems = '';
        if (at.ld) at.ld.style.display = 'none';
        if (at.nx) {
          at.nx = 0;
          at.lab.style.left = '';
        }
      }
      for (i = 0; i < ats.length; i++) {
        at = ats[i];
        at.i = i;
        var a = at.el.getBoundingClientRect();
        var mk = at.sk || at.el.querySelector('.shm__dot');
        var m = mk ? mk.getBoundingClientRect() : null;
        at.f = m && m.width ? [m.left - a.left, m.top - a.top, m.right - a.left, m.bottom - a.top] : null;
        at.on = !!(at.lab && at.lab.offsetWidth);
        if (!at.on) continue;
        var r = at.lab.getBoundingClientRect();
        at.home = [r.left - a.left, r.top - a.top];
        at.w = at.lab.offsetWidth;
        at.h = at.lab.offsetHeight;
        at.em = parseFloat(getComputedStyle(at.lab.firstElementChild || at.lab).fontSize) || 12;
        /* the middle of each line of the label, from its top */
        at.rows = [];
        for (var c = at.lab.firstElementChild; c; c = c.nextElementSibling) {
          var cr = c.getBoundingClientRect();
          if (cr.height) at.rows.push((cr.top + cr.bottom) / 2 - r.top);
        }
        at.area = at.el.classList.contains('shm__ar') || !at.f;
      }
      labsMeasured = true;
    }

    /* the passes of the search for a peak or town label, from the first:
       how far from its mark it may sit (in ems), whether it may cross a
       route (as little as it can), and whether it must sit nearer its own
       mark than any other and off the border (without that, it always
       gets a hairline). A town's name takes the first four, never crossing a route */
    var PASSES = [[0.9, 0, 1], [1.6, 0, 1], [2.4, 0, 1], [3.6, 0, 0], [1.2, 1, 1], [2.4, 1, 0]];

    /* the best spot for one label in one pass, as its box and its
       hairline, or null. A region's name has one pass: near its home, off
       the routes and the border, on its home's side of the border. put
       holds the labels placed so far, lines their hairlines */
    function spotFor(at, marks, put, lines, inkR, inkB, pass, count, o0, sc) {
      var P = PASSES[pass];
      var f = at.f || [0, 0, 0, 0];
      var w = at.w;
      var h = at.h;
      var em = at.em;
      var ax = at.sx;
      var ay = at.sy;
      var fx = (f[0] + f[2]) / 2;
      var fy = (f[1] + f[3]) / 2;
      var mine = [ax + f[0], ay + f[1], ax + f[2], ay + f[3]];
      var best = null;
      var n = 0;
      function inLand(x, y) {
        return land.isPointInFill(new DOMPoint(x / sc + o0.x, y / sc + o0.y));
      }
      var side = at.area && land && land.isPointInFill && o0 ? inLand(ax + at.home[0] + w / 2, ay + at.home[1] + h / 2) : null;
      function test(lx, ly, base) {
        var b0 = ax + lx;
        var b1 = ay + ly;
        var b = [b0, b1, b0 + w, b1 + h];
        if (b0 < 6 || b1 < 6 || b[2] > vw - 6 || b[3] > vh - 6) return;
        var ri = inkIn(inkR, b, 2);
        if (ri && (!P[1] || at.area)) return;
        /* the border through a name reads as a line struck through it,
           and the paper ring would cut it beside one; only a peak's name
           may end up there, in its last passes */
        var bi = inkB ? inkIn(inkB, b, 1) : 0;
        if (bi && (P[2] || !at.g)) return;
        var q;
        for (q = 0; q < put.length; q++) {
          var o = put[q];
          var sep = Math.max(em, o[4]);
          if (hits(b, o, sep * 1.5, sep * 0.45)) return;
          /* a line level with a line of a label less than three ems
             away would read on with it */
          if (Math.max(o[0] - b[2], b0 - o[2]) < sep * 3) {
            for (var r1 = 0; r1 < at.rows.length; r1++) {
              for (var r2 = 0; r2 < o[5].length; r2++) if (Math.abs(b1 + at.rows[r1] - o[5][r2]) < sep * 0.7) return;
            }
          }
        }
        /* how near the label is to the middle of its mark, and to others' */
        var d0 = at.area ? 0 : toBox(ax + fx, ay + fy, b);
        var amb = 0;
        for (q = 0; q < marks.length; q++) {
          var m = marks[q];
          if (m[4] === at.i) continue;
          if (gap(b, m) < 2) return;
          if (m[4] < 0 || at.area) continue;
          var d = toBox(m[5], m[6], b);
          if (P[2] && d < d0) return;
          amb = Math.max(amb, d0 + em - d);
        }
        for (q = 0; q < lines.length; q++) if (cuts(lines[q], [b0 - 2, b1 - 2, b[2] + 2, b[3] + 2])) return;
        /* a region's name stays on its home's side of the border */
        if (side !== null && inLand((b0 + b[2]) / 2, (b1 + b[3]) / 2) !== side) return;
        var ld = null;
        var lb = 0;
        var lr = 0;
        var g = at.area ? 0 : gap(b, mine);
        if (!at.area && (g > em * (at.g ? 0.8 : 1.2) || !P[2])) {
          ld = leadFor(at, b);
          /* a hairline at the border, or a few px beside it, reads as a
             second border line: where the label is plainly its mark's
             already and within this pass's reach, it goes; otherwise it may
             cross the border once, steeply. It may cross a route once, and
             run along one only where the label may cross routes */
          lb = ld && inkB ? inkOn(inkB, ld, 6) : 0;
          if (lb && P[2]) {
            if (g > em * P[0]) return;
            ld = null;
            lb = 0;
          } else if (lb === 2) {
            return;
          }
          lr = ld ? inkOn(inkR, ld, 6) : 0;
          if (lr === 2 && !P[1]) return;
          if (ld) {
            for (q = 0; q < marks.length; q++) if (marks[q][4] !== at.i && cuts(ld, [marks[q][0] - 2, marks[q][1] - 2, marks[q][2] + 2, marks[q][3] + 2])) return;
            for (q = 0; q < put.length; q++) if (cuts(ld, [put[q][0] - 2, put[q][1] - 2, put[q][2] + 2, put[q][3] + 2])) return;
            for (q = 0; q < lines.length; q++) if (crosses(ld, lines[q])) return;
          }
        }
        n++;
        if (count) return;
        var cost = base + Math.max(0, amb) * 0.5 + ri * 50 + bi * 100 + (lb ? 10 : 0) + (lr === 2 ? 100 : lr ? 30 : 0);
        if (!best || cost < best.cost) best = { x: lx, y: ly, b: b, ld: ld, cost: cost };
      }
      var lx;
      var ly;
      if (at.area) {
        for (ly = -Math.round(h * 2.5); ly <= h * 2.5; ly += 2) {
          for (lx = -Math.round(w * 0.8); lx <= w * 0.8; lx += 2) {
            test(at.home[0] + lx, at.home[1] + ly, Math.sqrt(lx * lx + ly * ly * 2.25) * 0.3);
          }
        }
      } else {
        var far = em * P[0];
        var hx = at.home[0];
        var hy = at.home[1];
        test(hx, hy, gap([hx, hy, hx + w, hy + h], f));
        var st = count ? 4 : 2;
        for (ly = Math.floor(f[1] - far - h); ly <= f[3] + far; ly += st) {
          for (lx = Math.floor(f[0] - far - w); lx <= f[2] + far; lx += st) {
            var g = gap([lx, ly, lx + w, ly + h], f);
            if (g < 2 || g > far) continue;
            test(lx, ly, g + Math.sqrt((lx - hx) * (lx - hx) + (ly - hy) * (ly - hy)) * 0.08);
          }
        }
      }
      return count ? n : best;
    }

    function rowsAt(at, top) {
      return at.rows.map(function (y) {
        return top + y;
      });
    }

    function placeLabels() {
      if (!labsMeasured) measureHomes();
      var o = x0y0();
      var s = k * cam.z;
      var inkR = inkMap(order, o, s);
      var inkB = edge ? inkMap([edge], o, s) : null;
      var marks = [];
      var todo = [];
      var i;
      var at;
      for (i = 0; i < ats.length; i++) {
        at = ats[i];
        at.sx = (at.x - o.x) * s;
        at.sy = (at.y - o.y) * s;
        if (at.f) marks.push([at.sx + at.f[0], at.sy + at.f[1], at.sx + at.f[2], at.sy + at.f[3], i, at.sx + (at.f[0] + at.f[2]) / 2, at.sy + (at.f[1] + at.f[3]) / 2]);
        if (!at.on) continue;
        /* a point off the view: edges() hides its label */
        if (at.sx < 0 || at.sx > vw || at.sy < 0 || at.sy > vh) continue;
        at.rank = at.g ? 0 : at.area ? 2 : 1;
        todo.push(at);
      }
      /* the map's own buttons, where they sit over it */
      var vr = view.getBoundingClientRect();
      var bs = [btn3d, allBtn];
      for (i = 0; i < bs.length; i++) {
        if (!bs[i] || bs[i].hidden || !bs[i].offsetWidth) continue;
        var br = bs[i].getBoundingClientRect();
        if (br.bottom > vr.top && br.top < vr.bottom) marks.push([br.left - vr.left, br.top - vr.top, br.right - vr.left, br.bottom - vr.top, -1]);
      }
      /* the peak with the fewest free spots near it goes first */
      for (i = 0; i < todo.length; i++) {
        at = todo[i];
        at.room = at.rank ? 0 : spotFor(at, marks, [], [], inkR, null, 2, true);
      }
      todo.sort(function (p, q) {
        return p.rank - q.rank || p.room - q.room || p.i - q.i;
      });
      var put = [];
      var lines = [];
      for (i = 0; i < todo.length; i++) {
        at = todo[i];
        var best = null;
        var last = at.area ? 1 : at.g ? PASSES.length : 4;
        for (var ps = 0; !best && ps < last; ps++) best = spotFor(at, marks, put, lines, inkR, inkB, ps, false, o, s);
        if (!best) {
          /* a peak keeps its home; a town or region is left out */
          if (at.g) put.push([at.sx + at.home[0], at.sy + at.home[1], at.sx + at.home[0] + at.w, at.sy + at.home[1] + at.h, at.em, rowsAt(at, at.sy + at.home[1])]);
          else if (at.area) at.lab.style.display = 'none';
          else at.el.style.display = 'none';
          continue;
        }
        at.el.style.display = '';
        at.lab.style.display = '';
        put.push(best.b.concat(at.em, [rowsAt(at, best.b[1])]));
        var lx = best.x;
        var ly = best.y;
        var home = lx === at.home[0] && ly === at.home[1];
        at.lab.style.translate = home ? '' : 'none';
        at.lab.style.transform = home ? '' : 'translate(' + lx.toFixed(1) + 'px,' + ly.toFixed(1) + 'px)';
        var f = at.f || [0, 0, 0, 0];
        var mid = lx + at.w / 2 - (f[0] + f[2]) / 2;
        at.lab.style.alignItems = home ? '' : Math.abs(mid) < at.w / 4 ? 'center' : mid < 0 ? 'flex-end' : 'flex-start';
        var ld = best.ld;
        if (ld) {
          lines.push(ld);
          if (!at.ld) {
            at.ld = document.createElement('i');
            at.ld.className = 'shm__lead';
            at.lab.appendChild(at.ld);
          }
          var dx = ld[2] - ld[0];
          var dy = ld[3] - ld[1];
          at.ld.style.width = Math.sqrt(dx * dx + dy * dy).toFixed(1) + 'px';
          at.ld.style.transform = 'translate(' + (ld[0] - best.b[0]).toFixed(1) + 'px,' + (ld[1] - best.b[1]).toFixed(1) + 'px) rotate(' + Math.atan2(dy, dx).toFixed(4) + 'rad)';
          at.ld.style.display = '';
        } else if (at.ld) {
          at.ld.style.display = 'none';
        }
      }
      labsDirty = true;
    }

    /* ---------- size ---------- */
    /* on a new size, and again when fonts arrive (force) */
    function measure(force) {
      var w = view.clientWidth;
      if (!w || (force !== true && labsMeasured && w === vw && view.clientHeight === vh)) return;
      vw = w;
      vh = view.clientHeight;
      k = vw / VW;
      labsMeasured = false;
      readout.w = 0;
      apply();
    }
    if (document.fonts) {
      /* only if a label changed size */
      var refit = function () {
        readout.w = 0;
        for (var i = 0; i < ats.length; i++) {
          var lw = ats[i].lab ? ats[i].lab.offsetWidth : 0;
          if (ats[i].lab && ats[i].lab.style.display !== 'none' && ats[i].el.style.display !== 'none' && lw !== (ats[i].on ? ats[i].w : 0)) {
            measure(true);
            return;
          }
        }
      };
      if (document.fonts.ready) document.fonts.ready.then(refit);
      if (document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', refit);
    }
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(view);
    else window.addEventListener('resize', measure);
    if (mqWide && mqWide.addEventListener) {
      mqWide.addEventListener('change', function () {
        if (pin) flyTo(frame(pin));
      });
    }

    root.classList.add('is-live');
    measure(true);

    /* ---------- a link to one route ---------- */
    var want = '';
    var want3d = false;
    try {
      var query = new URLSearchParams(window.location.search);
      want = query.get('route') || '';
      want3d = query.get('show') === '3d';
    } catch (err) {}
    if (want && routes[want]) {
      setPin(want, null, true);
      reveal(true);
    }
    /* ?show=3d, from the "See this route in 3D" links on trip pages and
       the homepage teaser: the 3D view opens straight away where it works.
       The parameter leaves the address first, so a reload after "Back to
       map" stays on the map. So does a #nepal-map (the teaser's, for
       browsers without 3D): going back to an address with it scrolls to
       it again as the view closes, which takes focus off the button. The
       map is scrolled into view here instead. */
    if (want3d) {
      try {
        var u = new URL(window.location.href);
        u.searchParams.delete('show');
        if (open3d) u.hash = '';
        history.replaceState(history.state, '', u.toString());
      } catch (err) {}
      if (open3d) {
        if (!(want && routes[want])) reveal(true);
        open3d(true);
      }
    }
  }

  function start() {
    var maps = document.querySelectorAll('[data-shm]');
    for (var i = 0; i < maps.length; i++) init(maps[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
